import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Avatar from "@mui/material/Avatar";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Wmlogo from "../../assets/images/brand/workhub-logo-dark.svg"
import { URLS } from "../../helpers/URLS";
import { downloadLink } from "../../helpers/constFIles";
import { setLoggedinUser } from "../../Redux/reducer";
import "./Navbar.scss";

const LINKS = [
  { to: '/about', label: 'About Us' },
  { to: '/plans', label: 'Plans' },
  { to: '/contact', label: 'Contact Us' },
];

/* useAxios is not used here on purpose: a signed-out visitor gets
   {status: 500} from the auth guard, which useAxios answers by redirecting
   to /login, and it would also flash the full-page loader on every visit. */
const baseUrl = '/api/v1/';

const ProfileMenu = ({ employee, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = employee.employeeName || '';
  const role = employee.totalData?.designation || employee.designation || '';
  const photoUrl = employee.profilePic ? downloadLink + employee.profilePic : undefined;

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className={`nav-profile${open ? ' is-open' : ''}`} ref={ref}>
      <button
        type="button"
        className="nav-profile-chip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <Avatar className="nav-profile-avatar" src={photoUrl} alt={name} sx={{ width: 34, height: 34 }}>
          {Array.from(name || '?')[0]}
        </Avatar>
        <span className="nav-profile-name">{name.split(' ')[0]}</span>
        <KeyboardArrowDownRoundedIcon className="nav-profile-caret" />
      </button>

      {open && (
        <div className="nav-profile-menu" role="menu">
          <div className="nav-profile-head">
            <b>{name}</b>
            {role && <em>{role}</em>}
            {employee.emailID && <span>{employee.emailID}</span>}
          </div>
          <button type="button" role="menuitem" onClick={() => go('/dashBoard')}>
            <GridViewRoundedIcon />Dashboard
          </button>
          <button type="button" role="menuitem" onClick={() => go('/profile')}>
            <PersonOutlineRoundedIcon />Profile
          </button>
          <button type="button" role="menuitem" className="is-danger" onClick={() => { setOpen(false); onLogout(); }}>
            <LogoutRoundedIcon />Logout
          </button>
        </div>
      )}
    </div>
  );
}

const NabBar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* The store already holds the user when arriving from inside the app;
     otherwise ask the server whether this browser has a session. */
  const storedUser = useSelector((state) => state.WorkHubAppSlice?.user);
  const employee = storedUser?.employee?.employeeCode ? storedUser.employee : null;

  useEffect(() => {
    if (employee) return undefined;
    let alive = true;
    axios.get(baseUrl + URLS.user)
      .then((res) => {
        if (alive && res.data && res.data.status === 200 && res.data.data) {
          dispatch(setLoggedinUser(res.data.data));
        }
      })
      .catch(() => { /* no session, or server unreachable: stay signed out */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.get(baseUrl + URLS.logout);
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoggedinUser({}));
    navigate('/');
  };

  /* the menu is React-controlled rather than Bootstrap's collapse, so it
     works without the CDN script and closes itself on navigation */
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className={`nav-bar${scrolled ? ' is-scrolled' : ''}${open ? ' is-open' : ''}`}>
      <div className="container">
        <NavLink to="/" className="nav-brand" aria-label="WorkHub home">
          <img src={Wmlogo} alt="WorkHub Manager" />
        </NavLink>

        <nav id="nav-menu" className="nav-menu" aria-label="Main">
          <div className="nav-center">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className="nav-link">
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="nav-actions">
            <NavLink to="/organization-register" className="nav-link">Organization</NavLink>
            {!employee && <NavLink to="/login" className="nav-link nav-cta">Login</NavLink>}
          </div>
        </nav>

        {/* outside the collapsible menu, so it stays in the bar on phones */}
        {employee && <ProfileMenu employee={employee} onLogout={logout} />}

        <button
          type="button"
          className="nav-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="nav-menu"
          aria-label={open ? 'Close navigation' : 'Open navigation'}
        >
          <span /><span /><span />
        </button>
      </div>
    </header>
  )
}

export default NabBar
